import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField, Button, Stack, Box, Select, MenuItem, InputLabel, FormControl, FormHelperText,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useSelection } from '../context/SelectionContext';

/**
 * A form for creating or updating a contact.
 * @param {object} props
 * @param {function} props.onSubmit - Function to call when the form is submitted.
 * @param {function} props.onCancel - Function to call when the cancel button is clicked.
 * @param {Array} props.businesses - List of businesses to populate the dropdown.
 * @param {object} [props.initialData=null] - The contact object to edit. If null, form is in "create" mode.
 */
const ContactForm = ({ onSubmit, onCancel, businesses, initialData = null, currentUserContactId }) => {
    // State to manage all form fields
    const { isAdmin, userBusinessId } = useAuth();
    const { showNotification } = useNotification();
    const { selectedBusinessId } = useSelection();
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        email: '',
        role: '',
        businessId: '',
        appAccess: false,
    });
    const [errors, setErrors] = useState({});

    const isEditing = Boolean(initialData);

    const normalizePhoneNumber = (value) => {
        if (!value) {
            return { error: 'Phone number is required.' };
        }
        const trimmed = value.trim();
        if (trimmed.startsWith('+') && /^\+\d{10,14}$/.test(trimmed)) {
            if (trimmed.startsWith('+91') && trimmed.length === 13) {
                return { value: trimmed };
            }
            return { value: trimmed };
        }
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length === 12 && digits.startsWith('91')) {
            return { value: `+${digits}` };
        }
        if (digits.length === 10) {
            return { value: `+91${digits}` };
        }
        return { error: 'Enter phone number with country code (e.g. +919876543210).' };
    };

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    // When in "edit" mode, this effect runs and populates the form with existing data.
    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.contactName || '',
                phoneNumber: initialData.phoneNumber || '',
                email: initialData.email || '',
                role: initialData.role || '',
                businessId: initialData.PK ? initialData.PK.replace('BUSINESS#', '') : '',
            });
        } else {
            const defaultBusiness = (!isAdmin && userBusinessId) ? userBusinessId : (selectedBusinessId || '');
            if (defaultBusiness) {
                setFormData(prev => ({ ...prev, businessId: defaultBusiness }));
            }
        }
    }, [initialData, isAdmin, userBusinessId, selectedBusinessId]);

    const isSupervisorEditingSelf = useMemo(() => (
        isEditing
        && initialData?.contactId
        && initialData.contactId === currentUserContactId
        && initialData.role === 'Supervisor'
    ), [isEditing, initialData, currentUserContactId]);

    useEffect(() => {
        if (isSupervisorEditingSelf && formData.role !== 'Supervisor') {
            setFormData(prev => ({ ...prev, role: 'Supervisor' }));
        }
    }, [isSupervisorEditingSelf, formData.role]);

    const roleOptions = useMemo(() => ([
        { value: 'Business Owner', label: 'Business Owner', disabled: isSupervisorEditingSelf },
        { value: 'Supervisor', label: 'Supervisor', disabled: false },
        { value: 'Employee', label: 'Employee', disabled: isSupervisorEditingSelf },
        { value: 'Guest', label: 'Guest', disabled: isSupervisorEditingSelf },
    ]), [isSupervisorEditingSelf]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'role') {
            const option = roleOptions.find(opt => opt.value === value);
            if (option?.disabled) {
                return;
            }
        }
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const nextErrors = {};
        if (!formData.name) nextErrors.name = 'Contact name is required.';
        let normalizedPhone = null;
        if (!formData.phoneNumber) {
            nextErrors.phoneNumber = 'Phone number is required.';
        } else {
            const result = normalizePhoneNumber(formData.phoneNumber);
            if (result.error) nextErrors.phoneNumber = result.error;
            else normalizedPhone = result.value;
        }
        const trimmedEmail = formData.email ? formData.email.trim() : '';
        if (!trimmedEmail) {
            nextErrors.email = 'Email address is required.';
        } else if (!isValidEmail(trimmedEmail)) {
            nextErrors.email = 'Enter a valid email address.';
        }
        if (!formData.role) nextErrors.role = 'Role is required.';
        if (!formData.businessId) nextErrors.businessId = 'Business is required.';

        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            showNotification({ message: 'Please correct the highlighted fields.', severity: 'warning' });
            return;
        }

        const payload = {
            ...formData,
            phoneNumber: normalizedPhone || formData.phoneNumber,
            email: trimmedEmail,
        };

        onSubmit(payload, initialData ? initialData.contactId : null);
        
        if (!isEditing) {
            setFormData({ name: '', phoneNumber: '', email: '', role: '', businessId: '', appAccess: false });
            setErrors({});
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    label="Contact Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                />
                <TextField
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.phoneNumber)}
                    helperText={errors.phoneNumber}
                />
                <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                />
                
                <FormControl fullWidth required>
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Select
                        labelId="role-select-label"
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        error={Boolean(errors.role)}
                    >
                        {roleOptions.map(option => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.role && <FormHelperText error>{errors.role}</FormHelperText>}
                    {isSupervisorEditingSelf && (
                        <FormHelperText>
                            Supervisors cannot change their role to Business Owner or Guest.
                        </FormHelperText>
                    )}
                </FormControl>
                
                <FormControl fullWidth required>
                    <InputLabel id="business-select-label">Business</InputLabel>
                    <Select
                        labelId="business-select-label"
                        label="Business"
                        name="businessId"
                        value={formData.businessId}
                        onChange={handleChange}
                        disabled={!isAdmin || isEditing}
                        error={Boolean(errors.businessId)}
                    >
                        {businesses.map(business => (
                            <MenuItem key={business.businessId} value={business.businessId}>
                                {business.businessName}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.businessId && <FormHelperText error>{errors.businessId}</FormHelperText>}
                </FormControl>
                <Box sx={{ pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {/* The new Cancel button */}
                    <Button variant="outlined" color="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {isEditing ? 'Save Changes' : 'Create Contact'}
                    </Button>
                </Box>
            </Stack>
        </form>
    );
};

export default ContactForm;
